import { withActionDialogLock } from "@/action-dialog/actionDialogLock";
import {
  ConfirmTrashCallable,
  PublishedFileDeleteCallable,
} from "@/action-dialog/callables";
import { orgAPI } from "@/api/api";
import type { FileRecord } from "@cohesive/api-client";

export interface DeletePublishedFileParams {
  record: FileRecord;
  perform: () => void | Promise<void>;
}

/**
 * The tier-aware gate in front of single-record file deletes:
 *
 * - A reference removes instantly — the Library file it points at (and its
 *   publish state) is untouched, so no confirmation applies.
 * - A Library file with live references warns first (D15): archiving it
 *   breaks those references until it's restored.
 * - A published file confirms that its public link will break.
 * - Everything else runs `perform` immediately with no dialog.
 */
export async function runDeletePublishedFileWorkflow(
  params: DeletePublishedFileParams,
): Promise<boolean> {
  const { record, perform } = params;

  if (record.kind === "link") {
    await perform();
    return true;
  }

  if (record.class === "workspace" && !record.path.endsWith("/")) {
    let referenceCount = 0;
    try {
      const { references } = await orgAPI.fileRecords.getReferences(record.id);
      referenceCount = references.length;
    } catch {
      // The count is advisory — fall through to the remaining gates.
    }
    if (referenceCount > 0) {
      const where =
        referenceCount === 1
          ? "1 canvas or chat references this file"
          : `${referenceCount} canvases and chats reference this file`;
      return withActionDialogLock(() =>
        ConfirmTrashCallable.call({
          kind: "file",
          name: record.path.split("/").filter(Boolean).pop(),
          children: `${where} — those references stop working until it's restored from the Archive.`,
          mutationFn: async (call) => {
            await perform();
            call.end(true);
          },
        }),
      );
    }
  }

  if (!record.published) {
    await perform();
    return true;
  }

  return withActionDialogLock(() =>
    PublishedFileDeleteCallable.call({
      mutationFn: async (call) => {
        await perform();
        call.end(true);
      },
    }),
  );
}
